import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Search, 
  ChevronRight,
  ChevronDown,
  Play,
  Moon,
  Sun,
  User,
  Settings,
  Bell,
  LogOut,
  Globe,
  Key,
  Building2,
  FileText,
  Code,
  Database,
  Loader2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuGroup,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '@/lib/contexts/OrganizationContext';
import { useUser } from '@/lib/contexts/UserContext';
import { useAuth } from '@/lib/auth/useAuth';
import { searchApi, SearchResult } from '@/api/search';
import { Command, CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { trackEvent, isFeatureEnabled } from '@/lib/analytics';
import { StaffTooltip } from '@/components/common/StaffTooltip';
import { useTheme } from '@/lib/contexts/ThemeContext';
import { isEvalOpsStaff } from '@/lib/utils/user';
import OnboardingProgressRing from './OnboardingProgressRing';
import { getApiKeys } from '@/api/api-keys';
import { OrganizationSwitcher } from './OrganizationSwitcher';

const TopBar = () => {
  const { organization, loading: orgLoading } = useOrganization();
  const { user, isLoading: userLoading } = useUser();
  const { user: authUser } = useAuth();
  const { logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [isRunning, setIsRunning] = useState(false);
  const [hasApiKeys, setHasApiKeys] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const { toast } = useToast();
  const navigate = useNavigate();

  const performSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      setIsSearchOpen(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await searchApi.search(query);
      setSearchResults(response.results);
      setIsSearchOpen(true);
      
      // Track search event when user submits a search query
      if (query.trim().length > 2) {
        trackEvent('search_used', {
          search_query: query,
          results_count: response.total || 0
        });
      }
    } catch (error) {
      console.error('Search error:', error);
      toast({
        title: 'Search Error',
        description: 'Failed to perform search',
        variant: 'destructive',
      });
    } finally {
      setIsSearching(false);
    }
  }, [setSearchResults, setIsSearchOpen, setIsSearching, toast]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Don't search if query is empty
    if (query.trim() === '') {
      setIsSearching(false);
      setSearchResults([]);
      setIsSearchOpen(false);
      return;
    }
    
    setIsSearching(true);
    setIsSearchOpen(true);
    
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(query);
    }, 300);
  };

  const handleSearchSelect = (result: SearchResult) => {
    // Reset search state
    setSearchQuery('');
    setSearchResults([]);
    setIsSearchOpen(false);
    
    // Navigate based on result type
    if (result.type === 'suite') {
      navigate(`/suites/${result.id}`);
    } else if (result.type === 'run') {
      navigate(`/runs/${result.id}`);
    } else if (result.type === 'test') {
      navigate(`/tests/${result.id}`);
    } else {
      // For documentation pages, use the path directly
      navigate(result.path);
    }
  };

  const handleToggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    toggleTheme();
    
    // Track theme toggle event
    trackEvent('theme_toggled', {
      new_theme: newTheme
    });
    
    // Show toast notification
    toast({
      title: 'Theme Changed',
      description: `The application theme has been switched to ${newTheme} mode`,
    });
  };

  const handleRunTests = () => {
    if (!isRunning) {
      // Track run tests clicked event
      trackEvent('run_tests_clicked', {
        invoked_from: 'dashboard',
        test_count_requested: 0 // Update with actual count if available
      });
      
      setIsRunning(true);
      
      // Add a mock delay before enabling the button again
      setTimeout(() => {
        setIsRunning(false);
        
        toast({
          title: "Tests Completed",
          description: `All tests have been executed successfully`,
        });
      }, 2000);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      toast({
        title: "Logged out",
        description: "You have been successfully logged out",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to log out. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getEnvironmentColor = (): string => {
    return 'bg-[#10b981]/20 text-[#10b981]';
  };

  const copyCLIToken = () => {
    // Navigate to integrations page where users can create and copy API keys
    navigate('/integrations');
    toast({
      title: "API Keys",
      description: "Navigate to the integrations page to create and copy CLI tokens",
    });
  };

  const handleKeyboardShortcut = useCallback((e: KeyboardEvent) => {
    // Cmd+K or Ctrl+K for search
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      searchInputRef.current?.focus();
    }
    
    // Cmd+/ or Ctrl+/ for advanced search
    if ((e.metaKey || e.ctrlKey) && e.key === '/') {
      e.preventDefault();
      searchInputRef.current?.focus();
    }
  }, [searchInputRef]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyboardShortcut);
    return () => {
      window.removeEventListener('keydown', handleKeyboardShortcut);
    };
  }, [handleKeyboardShortcut]);

  // Check for API keys to enable/disable Run Tests button
  useEffect(() => {
    const checkApiKeys = async () => {
      if (user?.id) {
        try {
          const response = await getApiKeys(user.id);
          setHasApiKeys(response.success && response.data && response.data.length > 0);
        } catch (error) {
          console.error('Error checking API keys:', error);
          setHasApiKeys(false);
        }
      }
    };

    checkApiKeys();
  }, [user?.id]);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (searchInputRef.current && !searchInputRef.current.contains(event.target as Node)) {
      setIsSearchOpen(false);
    }
  }, [searchInputRef, setIsSearchOpen]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Add handler for notification bell click
  const handleNotificationBellClick = () => {
    trackEvent('notification_bell_clicked', {
      unread_count: 0 // Update with actual count if available
    });
  };
  
  // Add handler for profile menu open
  const handleProfileMenuOpen = () => {
    trackEvent('profile_menu_opened', {
      menu_options_shown: 'profile,settings,cli_token,documentation,logout'
    });
  };

  return (
    <div className="h-16 border-b flex items-center justify-between px-6">
      <div className="flex items-center space-x-4 lg:space-x-8">
        <OrganizationSwitcher />
        
        <div className="flex items-center space-x-2">
          <div
            className={cn(
              "px-2 py-1 text-xs font-medium rounded-full",
              getEnvironmentColor()
            )}
          >
            PROD
          </div>
        </div>
      </div>

      <div className="flex items-center space-x-4">
        {/* Onboarding Progress Ring */}
        <OnboardingProgressRing />
        
        <div className="relative">
          <Input
            ref={searchInputRef}
            type="search"
            placeholder="Search... (⌘K)"
            className="w-[200px] lg:w-[300px]"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
          />
          {isSearching ? (
            <Loader2 className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin" />
          ) : (
            <Search className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          )}
          
          {isSearchOpen && searchResults.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-popover border rounded-md shadow-lg z-50">
              <div className="p-2">
                {searchResults.map((result) => (
                  <button
                    key={result.id}
                    onClick={() => handleSearchSelect(result)}
                    className="w-full flex items-center gap-2 p-2 rounded-sm hover:bg-accent cursor-pointer text-left"
                  >
                    {result.type === 'suite' && <FileText className="h-4 w-4" />}
                    {result.type === 'run' && <Code className="h-4 w-4" />}
                    {result.type === 'test' && <Database className="h-4 w-4" />}
                    <div className="flex flex-col">
                      <span>{result.title || 'Untitled'}</span>
                      {result.description && (
                        <span className="text-xs text-muted-foreground">{result.description}</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={handleRunTests}
                disabled={isRunning || !hasApiKeys}
              >
                <Play className="h-4 w-4" />
                {isRunning ? 'Running...' : 'Run Tests'}
              </Button>
            </TooltipTrigger>
            {!hasApiKeys && (
              <TooltipContent>
                <p>Install CLI & API key first—takes 30 sec.</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleToggleTheme}
              >
                {theme === 'dark' ? (
                  <Sun className="h-5 w-5" />
                ) : (
                  <Moon className="h-5 w-5" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Toggle theme</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" onClick={handleNotificationBellClick}>
              <Bell className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[300px]">
            <DropdownMenuLabel>Notifications</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <div className="max-h-[300px] overflow-y-auto">
              {/* Add notification items here */}
              <div className="p-4 text-center text-sm text-muted-foreground">
                No new notifications
              </div>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="gap-2" onClick={handleProfileMenuOpen}>
              {!userLoading && user && (
                <>
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-sm font-medium">
                      {user.name && typeof user.name === 'string' ? user.name.split(' ').map(n => n[0]).join('') : '?'}
                    </span>
                  </div>
                  <span className="hidden md:inline-block">{user.name || 'User'}</span>
                </>
              )}
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[200px]">
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            {authUser && user && isEvalOpsStaff(authUser.email) && isFeatureEnabled('staff-badge') && (
              <>
                <DropdownMenuSeparator />
                <div className="px-2 py-1.5">
                  <div className="text-xs font-medium text-muted-foreground mb-1">Staff Info</div>
                  <div className="text-xs space-y-0.5">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">ID:</span>
                      <span className="font-mono text-xs">{user.id.substring(0, 8)}...</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Role:</span>
                      <span>{user.role}</span>
                    </div>
                  </div>
                </div>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={() => navigate('/profile')}>
                <User className="mr-2 h-4 w-4" />
                <span>Profile</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/settings')}>
                <Settings className="mr-2 h-4 w-4" />
                <span>Settings</span>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={copyCLIToken}>
                <Key className="mr-2 h-4 w-4" />
                <span>Copy CLI Token</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => window.open('https://evalops.dev/docs/core-concepts', '_blank')}>
                <Globe className="mr-2 h-4 w-4" />
                <span>Documentation</span>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default TopBar;
